import React from "react";
import {
  Users,
  ShieldCheck,
  Clock,
  Leaf,
  Award,
  Sparkles,
  CheckCircle,
  Star
} from "lucide-react";
import { Link } from "react-router-dom";

const WhyChooseUsSection = () => {
  const features = [
    {
      icon: Users,
      title: "FULLY TRAINED TECHNICIANS",
      description: "Our professional cleaners are skilled and punctual.",
      color: "from-blue-500 to-indigo-600",
      iconColor: "text-blue-600",
      bgColor: "bg-blue-50"
    },
    {
      icon: ShieldCheck,
      title: "7-DAY SATISFACTION GUARANTEE",
      description: "We stand by our work ensuring you're fully satisfied.",
      color: "from-emerald-500 to-green-600",
      iconColor: "text-emerald-600",
      bgColor: "bg-emerald-50"
    },
    {
      icon: Clock,
      title: "SAME-DAY SERVICE",
      description: "Fast, reliable cleaning when you need it most.",
      color: "from-amber-500 to-orange-600",
      iconColor: "text-amber-600",
      bgColor: "bg-amber-50"
    },
    {
      icon: Leaf,
      title: "ECO FRIENDLY PRODUCTS",
      description: "Safe for kids, pets, and allergy sufferers.",
      color: "from-green-500 to-teal-600",
      iconColor: "text-green-600",
      bgColor: "bg-green-50"
    }
  ];

  const testimonials = [
    {
      rating: 5,
      text: "The best carpet cleaning service in Melbourne!",
      author: "Sarah M."
    },
    {
      rating: 5,
      text: "Professional, punctual, and eco-friendly.",
      author: "James T."
    },
    {
      rating: 5,
      text: "My carpets look brand new again!",
      author: "Lisa R."
    }
  ];

  return (
    <section className="py-16 md:py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-100 to-indigo-100 px-4 py-2 rounded-full mb-6">
            <Sparkles size={18} className="text-blue-600" />
            <span className="font-semibold text-blue-700">Premium Service</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Why Choose <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Our Cleaning Services</span>
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            We offer more than just clean carpet, here's why Knight Cleanings is the right choice
          </p>
          
          <div className="w-24 h-1.5 bg-gradient-to-r from-blue-500 to-indigo-500 mx-auto rounded-full" />
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {features.map((feature, index) => (
            <div 
              key={index} 
              className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border border-gray-100 overflow-hidden"
            >
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-500 to-indigo-500" />
              
              <div className="p-8">
                <div className={`${feature.bgColor} h-16 w-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <feature.icon size={28} className={feature.iconColor} />
                </div>
                
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                
                <p className="text-gray-600 mb-4">
                  {feature.description}
                </p>
                
                <div className="flex items-center gap-2">
                  <div className={`h-1.5 w-8 rounded-full bg-gradient-to-r ${feature.color}`} />
                  <div className={`h-1.5 w-4 rounded-full bg-gradient-to-r ${feature.color} opacity-60`} />
                  <div className={`h-1.5 w-2 rounded-full bg-gradient-to-r ${feature.color} opacity-40`} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Commitment Section */}
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl p-8 md:p-12 border border-gray-100 mb-16">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-3 rounded-full mb-8">
              <Award size={20} className="text-blue-600" />
              <span className="font-semibold text-blue-800">Our Commitment to Excellence</span>
            </div>
            
            <p className="text-2xl md:text-3xl font-semibold text-gray-900 mb-8">
              We're committed to providing high-quality <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">carpet cleaning services</span> across Melbourne
            </p>
            
            <div className="grid md:grid-cols-3 gap-8 mt-12">
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">5,000+</div>
                <div className="text-gray-600">Happy Customers</div>
              </div>
              
              <div className="text-center">
                <div className="text-4xl font-bold text-emerald-600 mb-2">98%</div>
                <div className="text-gray-600">Satisfaction Rate</div>
              </div>
              
              <div className="text-center">
                <div className="text-4xl font-bold text-amber-600 mb-2">24/7</div>
                <div className="text-gray-600">Emergency Service</div>
              </div>
            </div>
          </div>
        </div>

        {/* Testimonials */}
        <div className="max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-center text-gray-900 mb-10">
            What Our Customers Say
          </h3>
          
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index} 
                className="bg-white rounded-xl p-6 border border-gray-200 hover:border-blue-200 transition-all duration-300"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star 
                      key={i} 
                      size={18} 
                      className="text-amber-400 fill-amber-400" 
                    />
                  ))}
                </div>
                
                <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-100 to-indigo-100 flex items-center justify-center">
                    <CheckCircle size={16} className="text-blue-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.author}</div>
                    <div className="text-sm text-gray-500">Verified Customer</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Button */}
        <div className="text-center mt-16">
          <Link to="/booking" className="group bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-95 inline-flex items-center gap-3">
            <span>Book Your Cleaning Service</span>
            <Sparkles size={18} className="group-hover:rotate-12 transition-transform duration-300" />
          </Link>
          
          <p className="text-gray-600 mt-4 text-sm">
            No hidden fees • Free quotes • Flexible scheduling
          </p>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUsSection;